import { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useKleinerBildschirm } from '@/theme/kleinerBildschirm';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Variante A):
 * Generische kompakte Tabelle mit optionaler Auswahl und Sortierung.
 *
 * - Zeilendichte 42px (Feinschliff 1; Header bleibt kompakt 34px),
 *   kein Zebra, 0.5px divider-Trennlinien
 * - `render`-Funktion pro Column ist selbst zuständig für Look
 *   der Zelle (Avatar+Name, Beträge, etc.)
 * - Zahlen-Zellen (align="right") bekommen automatisch tabular-nums
 * - Auswahl und Sortierung sind controlled (Parent-State)
 */

export type SortDirection = 'asc' | 'desc';

export interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  // Breiten-Hint (Feinschliff 1): table-layout bleibt `auto`, width
  // steuert die Verteilung (Telefon schmal, E-Mail breit, ...).
  width?: number | string;
  // Kappt überlange Werte per Ellipsis statt Umbruch/Gequetsche.
  // Wirkt als Breiten-Deckel nur zusammen mit `width` (maxWidth).
  ellipsis?: boolean;
  /**
   * Zellen-Innenabstand auf 4px statt 12px (Alex 10.09.2026, Variante A).
   *
   * 🔥 GEBRAUCHT FUER SPALTEN MIT EINEM EINGABEFELD. Die Zeile ist auf
   * `ROW_MIN_HEIGHT` (42px) ausgelegt: 12px Padding + 18px Textzeile + 12px.
   * Ein Textfeld im Hausmass ist 32px hoch — mit dem Standard-Padding waere
   * die Zelle 56px und die Zeile wuechse um ein Drittel, sobald irgendwo ein
   * Feld steht. Mit 4px sind es 40px, die Zeile bleibt bei 42px.
   *
   * Additiv: keine Bestandsspalte traegt das Flag, keine bestehende Tabelle
   * aendert sich. Es ist bewusst KEIN freier Padding-Wert — sonst waere es
   * die Tuer, durch die jede Tabelle ihre eigenen Abstaende bekommt.
   */
  kompakt?: boolean;
  /**
   * Zusatz hinter dem Spaltentitel, typisch ein Info-i (W4 Telefonie,
   * 23.09.2026: „Arbeitszeit (h)" braucht seine Definition an der Spalte,
   * nicht nur an der Kachel). Additiv wie `kompakt`: ohne das Feld aendert
   * sich keine Tabelle. Ein Klick-Stopper ist Sache des Zusatzes selbst
   * (`InfoTipp stopKlick`), sonst sortiert ein Klick auf das i die Spalte.
   */
  headerZusatz?: ReactNode;
  /**
   * Tooltip am Spaltentitel, z. B. der volle Name hinter einem Kurznamen
   * (Cockpit „Nach Gesellschaft", Alex 23.10.2026). Additiv wie `kompakt`:
   * ohne das Feld aendert sich keine Tabelle.
   */
  headerTooltip?: string;
  /**
   * Spalte bleibt beim seitlichen Scrollen stehen (Cockpit-Terminverteilung,
   * Alex 06.10.2026: Namens- und Summenspalte fixiert). Additiv wie
   * `kompakt`: ohne das Feld aendert sich keine Tabelle. Die Zelle bekommt
   * den Kartenhintergrund, damit gescrollter Inhalt nicht durchscheint.
   */
  sticky?: 'left' | 'right';
  /**
   * Spalte entfaellt auf kleinen Bildschirmen (Kompakt-Modus, Decision #247,
   * Schwelle `KLEINER_BILDSCHIRM_BREITE`). Fuer Spalten, die man zum Arbeiten
   * nicht braucht — der Wert steht im Fall bzw. Kunden ohnehin.
   */
  aufKleinemBildschirmAus?: boolean;
  render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;

  selectable?: boolean;
  selectedIds?: Set<string | number>;
  onToggleSelect?: (id: string | number) => void;
  onToggleAll?: () => void;
  isRowSelectable?: (row: T) => boolean;

  sortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (key: string) => void;
  // Multi-Sort-Anzeige (z.B. Benutzer-Tabelle: akkumulierendes
  // Sort-Array). Wenn gesetzt, gewinnt sortEntries gegen
  // sortKey/sortDirection — jede enthaltene Spalte zeigt ihren
  // Pfeil. Die Sort-Logik bleibt beim Parent; onSort liefert
  // weiterhin den geklickten key.
  sortEntries?: { key: string; direction: SortDirection }[];

  onRowClick?: (row: T) => void;
  /**
   * `data-testid` je Zeile. Additiv und optional — ohne die Prop rendert die
   * Tabelle wie bisher.
   *
   * Gebraucht, seit ReklamationenView von einer eigenen `<TableRow>` auf
   * DataTable umgestellt wurde (10.09.2026): dort haengen die Zeilen-Tests an
   * `rekla-<dealId>` und lesen mit `within(zeile)` darin weiter. Ohne diesen
   * Haken bliebe nur, DataTable NICHT zu benutzen — also genau die Abweichung,
   * die der Umbau beseitigen soll.
   */
  getRowTestId?: (row: T) => string;
  emptyState?: ReactNode;
  footer?: ReactNode;
  /**
   * `table-layout: fixed` statt `auto`. Ohne das richtet sich jede
   * Spaltenbreite nach dem laengsten Inhalt der aktuellen Seite — beim
   * Sortieren wechselt der, und die Tabelle springt (Sichttest 30.07.).
   * Opt-in, damit bestehende Tabellen ihre Auto-Breiten behalten; sinnvoll
   * nur zusammen mit `width` an den Spalten.
   */
  fixedLayout?: boolean;
  /**
   * Design-Test Stufe 2 (25.10.2026): Aussenrahmen der Tabelle. Default an
   * (Bestand); in einer Karte (Fall-Ansicht) aus, sonst Rahmen im Rahmen.
   */
  rahmen?: boolean;

  'aria-label'?: string;
}

// Design-Test Stufe 1/2 (25.10.2026): Tabellen-Regel — Zeilen 44px, Hairline,
// Kopf 12px/500 grau ohne Uppercase, Zellen 14px dunkel. Schrift kommt aus den
// Theme-Rollen 'label' (Kopf) und 'wert' (Zelle).
const HEAD_ROW_HEIGHT = 44;
const ROW_MIN_HEIGHT = 44;
// Kompakt-Modus, Decision #247: unter der Schwelle 36px, Schrift unveraendert.
const ROW_HEIGHT_KLEIN = 36;
// Feste Breite der Auswahlspalte auf kleinen Bildschirmen — die erste
// Datenspalte steht dann bei genau diesem Versatz still.
const AUSWAHL_BREITE_KLEIN = 40;

/** Fixierte Spalte (`Column.sticky`) — Kopf und Zeilen gleich. */
const stickySx = (seite?: 'left' | 'right', versatz = 0) => (seite
  ? {
    position: 'sticky' as const,
    [seite]: versatz,
    zIndex: 1,
    bgcolor: 'background.paper',
  }
  : {});

const DataTable = <T,>({
  columns: alleSpalten,
  rows,
  getRowId,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  isRowSelectable,
  sortKey,
  sortDirection,
  onSort,
  sortEntries,
  onRowClick,
  getRowTestId,
  emptyState,
  footer,
  fixedLayout = false,
  rahmen = true,
  'aria-label': ariaLabel,
}: DataTableProps<T>) => {
  // Kleiner Bildschirm: unwichtige Spalten raus, Zeilen niedriger, erste
  // Spalte (und die Auswahl davor) steht beim seitlichen Scrollen still.
  const klein = useKleinerBildschirm();
  const columns = klein
    ? alleSpalten.filter((c) => !c.aufKleinemBildschirmAus)
    : alleSpalten;
  const kopfHoehe = klein ? ROW_HEIGHT_KLEIN : HEAD_ROW_HEIGHT;
  const zeilenHoehe = klein ? ROW_HEIGHT_KLEIN : ROW_MIN_HEIGHT;
  const auswahlSticky = klein && selectable
    ? { ...stickySx('left'), width: AUSWAHL_BREITE_KLEIN, minWidth: AUSWAHL_BREITE_KLEIN, px: 2 }
    : {};
  const spaltenSticky = (col: Column<T>, index: number) => {
    if (col.sticky) return stickySx(col.sticky);
    if (klein && index === 0) return stickySx('left', selectable ? AUSWAHL_BREITE_KLEIN : 0);
    return {};
  };
  const totalCols = columns.length + (selectable ? 1 : 0);
  const selectableRows = isRowSelectable
    ? rows.filter(isRowSelectable)
    : rows;
  const allSelected =
    selectable
    && selectableRows.length > 0
    && selectableRows.every((r) => selectedIds?.has(getRowId(r)));
  const someSelected =
    selectable
    && selectableRows.some((r) => selectedIds?.has(getRowId(r)))
    && !allSelected;

  return (
    <TableContainer
      sx={{
        ...(rahmen && { border: '1px solid', borderColor: 'divider', borderRadius: '8px' }),
        bgcolor: 'background.paper',
        // Breite Tabellen scrollen horizontal statt zu clippen
        // (z.B. Benutzer-Tabelle mit 9 Spalten auf schmalen Screens).
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      <Table aria-label={ariaLabel} sx={{ tableLayout: fixedLayout ? 'fixed' : 'auto' }}>
        <TableHead>
          <TableRow sx={{ height: kopfHoehe }}>
            {selectable && (
              <TableCell
                padding="checkbox"
                sx={{
                  py: 1,
                  px: 4,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  ...auswahlSticky,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onToggleAll}
                  disabled={!onToggleAll || selectableRows.length === 0}
                  inputProps={{ 'aria-label': 'Alle auswaehlen' }}
                  sx={{ p: 0 }}
                />
              </TableCell>
            )}
            {columns.map((col, index) => {
              const multiEntry = sortEntries?.find((s) => s.key === col.key);
              const isSorted = sortEntries ? !!multiEntry : sortKey === col.key;
              const direction = sortEntries ? multiEntry?.direction : sortDirection;
              const nextArrow: string = isSorted
                ? direction === 'asc'
                  ? '↑'
                  : '↓'
                : '';
              const clickable = col.sortable && !!onSort;
              return (
                <TableCell
                  key={col.key}
                  align={col.align ?? 'left'}
                  sx={{
                    py: 2,
                    px: 4,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    typography: 'label',
                    cursor: clickable ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    ...(col.width != null && { width: col.width }),
                    ...spaltenSticky(col, index),
                  }}
                  onClick={clickable ? () => onSort?.(col.key) : undefined}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}
                    gap={0.5}
                  >
                    {col.headerTooltip
                      ? <Tooltip title={col.headerTooltip} arrow><span>{col.header}</span></Tooltip>
                      : <span>{col.header}</span>}
                    {col.headerZusatz}
                    {nextArrow && (
                      <Typography component="span" variant="label">
                        {nextArrow}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={totalCols}
                align="center"
                sx={{
                  py: 6,
                  typography: 'meta',
                  borderBottom: 'none',
                }}
              >
                {emptyState ?? 'Keine Eintraege'}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => {
            const id = getRowId(row);
            const selected = selectedIds?.has(id) ?? false;
            const canSelect = !isRowSelectable || isRowSelectable(row);
            return (
              <TableRow
                key={id}
                data-testid={getRowTestId?.(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                selected={selected}
                sx={{
                  height: zeilenHoehe,
                  cursor: onRowClick ? 'pointer' : 'default',
                  bgcolor: selected ? 'primary.hover' : 'transparent',
                  transition: 'background-color 150ms',
                  '&:hover': {
                    bgcolor: selected ? 'primary.hover' : 'background.card',
                  },
                  '&:last-of-type td': { borderBottom: 'none' },
                }}
              >
                {selectable && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      py: 0.5,
                      px: 4,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      ...auswahlSticky,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={selected}
                      disabled={!canSelect || !onToggleSelect}
                      onChange={onToggleSelect ? () => onToggleSelect(id) : undefined}
                      inputProps={{ 'aria-label': `Zeile ${id} auswaehlen` }}
                      sx={{ p: 0 }}
                    />
                  </TableCell>
                )}
                {columns.map((col, index) => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    sx={{
                      py: col.kompakt ? 1 : 3,
                      px: 4,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      typography: 'wert',
                      ...(col.align === 'right' && {
                        fontVariantNumeric: 'tabular-nums',
                      }),
                      ...spaltenSticky(col, index),
                      ...(col.width != null && { width: col.width }),
                      ...(col.ellipsis && {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        ...(col.width != null && { maxWidth: col.width }),
                      }),
                    }}
                  >
                    {col.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {footer && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 4,
            py: 2,
            bgcolor: 'background.paper',
            typography: 'klein',
          }}
        >
          {footer}
        </Box>
      )}
    </TableContainer>
  );
};

export default DataTable;
